/**
 * File Field Renderer Component
 *
 * Purpose: Renders file upload field with drag & drop, progress tracking, and file management.
 *          Compact inline design that matches MUI outlined input styling.
 *
 * Architecture: Field renderer for file datatype in metadata forms
 *
 * Dependencies: React, Material-UI, fileService
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  AttachFile as AttachIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  Description as DocIcon,
  FolderZip as ArchiveIcon,
  TableChart as SpreadsheetIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import type { FieldRendererProps } from './types';
import { fileService } from '../../services/fileService';
import type { FileMetadata, UploadProgress, FileFieldConfig } from '../../types/file';

// =============================================================================
// TYPES
// =============================================================================

interface FileFieldValue {
  file_ids: string[];
  has_files: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get small icon based on MIME type
 */
const getFileIcon = (mimeType: string): React.ReactNode => {
  const sx = { fontSize: 16 };
  if (mimeType.startsWith('image/')) return <ImageIcon sx={sx} />;
  if (mimeType.startsWith('video/')) return <VideoIcon sx={sx} />;
  if (mimeType.startsWith('audio/')) return <AudioIcon sx={sx} />;
  if (mimeType === 'application/pdf') return <PdfIcon sx={sx} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') {
    return <SpreadsheetIcon sx={sx} />;
  }
  if (mimeType.includes('document') || mimeType.includes('word') || mimeType.includes('text')) {
    return <DocIcon sx={sx} />;
  }
  if (mimeType.includes('zip') || mimeType.includes('compressed')) {
    return <ArchiveIcon sx={sx} />;
  }
  return <FileIcon sx={sx} />;
};

/**
 * Parse file value from different formats
 */
const parseFileValue = (value: any): FileFieldValue => {
  if (!value) {
    return { file_ids: [], has_files: false };
  }
  if (Array.isArray(value)) {
    return { file_ids: value, has_files: value.length > 0 };
  }
  if (typeof value === 'object' && 'file_ids' in value) {
    return {
      file_ids: value.file_ids || [],
      has_files: value.has_files || false,
    };
  }
  return { file_ids: [], has_files: false };
};

// =============================================================================
// FILE FIELD RENDERER COMPONENT
// =============================================================================

export const FileFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
  disabled,
  readOnly,
  entityId,
}) => {
  const label = field.label_override || field.field_definition.label;
  const fileConfig = field.field_definition.file_config;

  const config: FileFieldConfig = fileConfig || {
    allowed_extensions: [
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
      'ppt',
      'pptx',
      'jpg',
      'jpeg',
      'png',
      'gif',
    ],
    max_size_mb: 50,
    max_files: 10,
    generate_thumbnails: true,
    allowed_mime_types: null,
  };

  // State
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileValue = parseFileValue(value);

  // Load existing files on mount or when value changes
  useEffect(() => {
    const loadFiles = async () => {
      if (fileValue.file_ids.length === 0) {
        setFiles([]);
        setLoadingFiles(false);
        return;
      }

      setLoadingFiles(true);
      try {
        const loadedFiles: FileMetadata[] = [];
        const loadedThumbnails: Record<string, string> = {};

        for (const fileId of fileValue.file_ids) {
          try {
            const metadata = await fileService.getMetadata(fileId);
            loadedFiles.push(metadata);

            if (metadata.has_thumbnail) {
              try {
                const thumbnailUrl = await fileService.getThumbnailUrl(fileId);
                loadedThumbnails[fileId] = thumbnailUrl;
              } catch {
                // Thumbnail failed to load
              }
            }
          } catch {
            console.error(`Failed to load file ${fileId}`);
          }
        }

        setFiles(loadedFiles);
        setThumbnails(loadedThumbnails);
      } catch (err) {
        console.error('Failed to load files:', err);
        setLocalError('Failed to load existing files');
      } finally {
        setLoadingFiles(false);
      }
    };

    loadFiles();
  }, [JSON.stringify(fileValue.file_ids)]);

  // Cleanup thumbnail URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(thumbnails).forEach((url) => {
        window.URL.revokeObjectURL(url);
      });
    };
  }, [thumbnails]);

  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: string } => {
      return fileService.validateFile(file, config);
    },
    [config]
  );

  const handleFileSelect = useCallback(
    async (selectedFiles: FileList | null) => {
      if (!selectedFiles || selectedFiles.length === 0) return;
      setLocalError(null);

      const currentCount = files.length;
      const remainingSlots = config.max_files - currentCount;

      if (remainingSlots <= 0) {
        setLocalError(`Maximum ${config.max_files} files allowed`);
        return;
      }

      const filesToUpload = Array.from(selectedFiles).slice(0, remainingSlots);

      for (const file of filesToUpload) {
        const validation = validateFile(file);
        if (!validation.valid) {
          setLocalError(validation.error || 'File validation failed');
          continue;
        }

        const progressEntry: UploadProgress = {
          filename: file.name,
          progress: 0,
          status: 'uploading',
        };

        setUploads((prev) => [...prev, progressEntry]);

        try {
          const response = await fileService.upload(
            file,
            {
              tenant_id: field.tenant_id,
              model_id: field.model_id,
              entity_id: entityId || '',
              fielddef_id: field.fielddef_id,
              file_config: config,
            },
            (progress) => {
              setUploads((prev) =>
                prev.map((u) => (u.filename === file.name ? { ...u, progress } : u))
              );
            }
          );

          if (response.success && response.file) {
            setUploads((prev) =>
              prev.map((u) =>
                u.filename === file.name
                  ? { ...u, status: 'completed', file_id: response.file!.file_id }
                  : u
              )
            );

            setFiles((prev) => [...prev, response.file!]);

            const newFileIds = [...fileValue.file_ids, response.file.file_id];
            onChange({ file_ids: newFileIds, has_files: true });

            if (response.file.has_thumbnail) {
              try {
                const thumbnailUrl = await fileService.getThumbnailUrl(response.file.file_id);
                setThumbnails((prev) => ({
                  ...prev,
                  [response.file!.file_id]: thumbnailUrl,
                }));
              } catch {
                // Thumbnail failed to load
              }
            }

            setTimeout(() => {
              setUploads((prev) => prev.filter((u) => u.filename !== file.name));
            }, 1000);
          } else {
            setUploads((prev) =>
              prev.map((u) =>
                u.filename === file.name ? { ...u, status: 'failed', error: response.message } : u
              )
            );
          }
        } catch (err: any) {
          setUploads((prev) =>
            prev.map((u) =>
              u.filename === file.name
                ? { ...u, status: 'failed', error: err.message || 'Upload failed' }
                : u
            )
          );
        }
      }
    },
    [field, files, fileValue, config, onChange, validateFile, entityId]
  );

  const handleDelete = useCallback(
    async (fileId: string) => {
      try {
        await fileService.delete(fileId);

        setFiles((prev) => prev.filter((f) => f.file_id !== fileId));

        if (thumbnails[fileId]) {
          window.URL.revokeObjectURL(thumbnails[fileId]);
          setThumbnails((prev) => {
            const next = { ...prev };
            delete next[fileId];
            return next;
          });
        }

        const newFileIds = fileValue.file_ids.filter((id) => id !== fileId);
        onChange({
          file_ids: newFileIds,
          has_files: newFileIds.length > 0,
        });
      } catch (err) {
        console.error('Failed to delete file:', err);
        setLocalError('Failed to delete file');
      }
    },
    [fileValue, onChange, thumbnails]
  );

  const handleDownload = useCallback(async (file: FileMetadata) => {
    try {
      await fileService.download(file.file_id, file.original_filename);
    } catch (err) {
      console.error('Failed to download file:', err);
      setLocalError('Failed to download file');
    }
  }, []);

  // Drag handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !readOnly) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!disabled && !readOnly) handleFileSelect(e.dataTransfer.files);
  };

  const handleClick = () => {
    if (!disabled && !readOnly && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const acceptString = config.allowed_extensions.map((ext) => `.${ext}`).join(',');
  const canUpload = !disabled && !readOnly && files.length < config.max_files;
  const hasError = (touched && !!error) || !!localError;
  const displayError = touched && error ? error : localError;

  // Border color logic matching MUI outlined input
  const borderColor = hasError ? 'error.main' : isDragging ? 'primary.main' : 'rgba(0, 0, 0, 0.23)';

  return (
    <Box>
      {/* Compact inline dropzone — matches MUI outlined input height */}
      {canUpload && (
        <Box
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
          data-testid="file-dropzone"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            mt: 0.5,
            width: '100%',
            height: 40,
            border: 1,
            borderColor,
            borderRadius: 1,
            cursor: 'pointer',
            bgcolor: isDragging ? 'action.hover' : 'background.paper',
            transition: 'border-color 0.2s, background-color 0.2s',
            '&:hover': {
              borderColor: hasError ? 'error.main' : 'text.primary',
              bgcolor: 'action.hover',
            },
            position: 'relative',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptString}
            multiple={config.max_files > 1}
            onChange={handleInputChange}
            style={{ display: 'none' }}
            data-testid="file-input"
          />

          <AttachIcon sx={{ fontSize: 20, color: 'action.active', flexShrink: 0 }} />

          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ flex: 1, userSelect: 'none' }}
          >
            {isDragging ? 'Drop files here' : 'Drop files or click to browse'}
          </Typography>

          <Chip
            label={`${files.length}/${config.max_files}`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
          />

          {/* Floating label */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: -9,
              left: 10,
              px: 0.5,
              bgcolor: 'background.paper',
              color: hasError ? 'error.main' : 'text.secondary',
              lineHeight: 1,
            }}
          >
            {label}
            {field.is_required && ' *'}
          </Typography>
        </Box>
      )}

      {/* Helper text */}
      <Typography
        variant="caption"
        color={hasError ? 'error.main' : 'text.secondary'}
        sx={{ mt: 0.5, ml: 1.75, display: 'block' }}
      >
        {displayError
          ? displayError
          : `.${config.allowed_extensions.join(', .')} \u00B7 ${config.max_size_mb} MB max`}
      </Typography>

      {/* Upload progress — slim inline bars */}
      {uploads.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {uploads.map((upload, index) => (
            <Box key={`${upload.filename}-${index}`} sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadIcon sx={{ fontSize: 14, color: 'action.active' }} />
                <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                  {upload.filename}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {upload.status === 'uploading' ? `${upload.progress}%` : upload.status}
                </Typography>
              </Box>
              {upload.status === 'uploading' && (
                <LinearProgress
                  variant="determinate"
                  value={upload.progress}
                  sx={{ mt: 0.25, height: 2, borderRadius: 1 }}
                />
              )}
              {upload.status === 'failed' && upload.error && (
                <Typography variant="caption" color="error" sx={{ ml: 2.5 }}>
                  {upload.error}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* File list — compact rows */}
      {loadingFiles ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={18} />
        </Box>
      ) : files.length > 0 ? (
        <Box sx={{ mt: 0.5 }}>
          {files.map((file) => (
            <Box
              key={file.file_id}
              data-testid={`file-item-${file.file_id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ color: 'action.active', display: 'flex', flexShrink: 0 }}>
                {getFileIcon(file.mime_type)}
              </Box>

              <Typography
                variant="body2"
                noWrap
                title={file.original_filename}
                sx={{ flex: 1, fontSize: '0.8125rem' }}
              >
                {file.original_filename}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {file.file_size_display}
              </Typography>

              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={() => handleDownload(file)}
                  disabled={disabled}
                  data-testid={`download-${file.file_id}`}
                  sx={{ p: 0.25 }}
                >
                  <DownloadIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>

              {!readOnly && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(file.file_id)}
                    disabled={disabled}
                    data-testid={`delete-${file.file_id}`}
                    sx={{ p: 0.25 }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      ) : !canUpload ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
          No files uploaded
        </Typography>
      ) : null}
    </Box>
  );
};

export default FileFieldRenderer;
